import { useTranslation } from "react-i18next";
import type { ConversationListItemDto, ConversationListSortName } from "@multi-chat/shared";
import { LayoutGroup, motion, useReducedMotion } from "motion/react";
import { cn } from "../../../lib/utils.js";
import { useConversationDraftSnapshot } from "../conversation-draft-context.js";
import {
  conversationListActivityAt,
  conversationListRowPreview,
  orderConversationsByEffectiveActivity,
} from "../conversation-draft-presentation.js";
import { formatConversationFullDate, formatConversationListTime } from "../conversation-time.js";
import { conversationTitle } from "../inbox-display.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import { useNowMs } from "../use-now-ms.js";
import { ConversationListRow } from "./ConversationListRow.js";

const LOAD_MORE_THRESHOLD_PX = 96;
const LIST_TIME_REFRESH_MS = 60_000;

const listLayoutTransition = {
  duration: 0.2,
  ease: [0.22, 1, 0.36, 1] as const,
};

interface ConversationListProps {
  items: ConversationListItemDto[];
  selectedConversationId: number | null;
  hasMore: boolean;
  loadingMore: boolean;
  refreshing?: boolean;
  sort?: ConversationListSortName;
  onSelect: (conversationId: number) => void;
  onLoadMore: () => boolean;
}

export function ConversationList({
  items,
  selectedConversationId,
  hasMore,
  loadingMore,
  refreshing = false,
  sort = "activity_desc",
  onSelect,
  onLoadMore,
}: ConversationListProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const drafts = useConversationDraftSnapshot();
  const nowMs = useNowMs(LIST_TIME_REFRESH_MS);
  const presentedItems = orderConversationsByEffectiveActivity(items, drafts, sort);

  function handleScroll(event: React.UIEvent<HTMLUListElement>): void {
    if (!hasMore || loadingMore) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= LOAD_MORE_THRESHOLD_PX) {
      onLoadMore();
    }
  }

  return (
    <LayoutGroup>
      {/* Flex column: the ul must fill this pane height so overflow-y-auto can scroll. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-inbox-rows">
        <motion.ul
          className={cn(
            "m-0 flex min-h-0 flex-1 list-none flex-col overflow-y-auto overscroll-contain px-0 pt-1.5 pb-3.5",
            INBOX_SCROLLER_CLASS,
          )}
          role="listbox"
          aria-label={t("inbox.listLabel")}
          aria-busy={refreshing || undefined}
          onScroll={handleScroll}
          initial={false}
          animate={
            reduceMotion
              ? { opacity: 1, filter: "none" }
              : {
                  opacity: refreshing ? 0.84 : 1,
                  filter: refreshing ? "saturate(0.96)" : "saturate(1)",
                }
          }
          transition={reduceMotion ? { duration: 0 } : { duration: 0.18, ease: "easeOut" }}
        >
          {presentedItems.map((item) => {
            const activityAt = conversationListActivityAt(item, drafts);
            const selected = item.conversationId === selectedConversationId;
            return (
              <motion.li
                key={item.conversationId}
                role="option"
                aria-selected={selected}
                layout={reduceMotion ? false : "position"}
                transition={reduceMotion ? { duration: 0 } : listLayoutTransition}
              >
                <ConversationListRow
                  item={item}
                  title={conversationTitle(item, t)}
                  preview={conversationListRowPreview(item, drafts, t)}
                  time={activityAt ? formatConversationListTime(activityAt, i18n.language, nowMs, t) : ""}
                  fullTime={activityAt ? formatConversationFullDate(activityAt, i18n.language) : ""}
                  activityAt={activityAt}
                  selected={selected}
                  onSelect={onSelect}
                />
              </motion.li>
            );
          })}
          {loadingMore ? (
            <li className="px-4 py-3 text-center text-[0.8rem] text-inbox-subtle" aria-live="polite">
              {t("inbox.loadingMoreConversations")}
            </li>
          ) : null}
        </motion.ul>
        {refreshing ? (
          <motion.div
            className="pointer-events-none absolute inset-x-3 top-2 z-[1] h-10 rounded-2xl bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.65)_20%,rgba(255,255,255,0.85)_50%,rgba(255,255,255,0.65)_80%,transparent_100%)]"
            aria-hidden="true"
            initial={false}
            animate={
              reduceMotion
                ? { opacity: 0.55 }
                : { opacity: [0.28, 0.5, 0.28], x: ["-8%", "0%", "8%"] }
            }
            transition={
              reduceMotion
                ? { duration: 0 }
                : { duration: 0.9, ease: "easeInOut", repeat: Infinity }
            }
          />
        ) : null}
      </div>
    </LayoutGroup>
  );
}
